export interface InnoFlash {
  id: string;
  filename: string;
  title: string;
  description: string; // Short 2-3 sentence summary for cards
  fullDescription: string; // Full detailed description for modal
  author: string;
  date: string;
  category: 'transit' | 'spatial' | 'data' | 'tools' | 'general';
  tags: string[];
  wordCount: number;
  imagesAvailable: number;
  previewImage?: string;
  relatedTools?: string[]; // IDs of related data team services
  additionalImages?: string[]; // Additional images for modal gallery
  methodology?: string; // Brief "how it works" explanation
  realWorldExample?: string; // Mention of actual project using this
  pdfPath?: string;
  pdfSizeM?: number;
}

export const innoflashes: InnoFlash[] = [
  {
    id: '18',
    filename: '25-08-17_InnovatieFlash18.md',
    title: 'OV-snelheden: Visualisatie van rijsnelheden, halteringstijden en frequentie',
    description: 'Visualisatie van het OV-systeem met focus op rijsnelheden, halteringstijden en frequentie volgens de dienstregeling. Laat zien waar metro\'s en trams snel of langzaam zijn, inclusief stilstandstijd per halte.',
    fullDescription: 'Een nieuwe visualisatie van het OV-systeem met focus op a) rijsnelheden, b) halteringstijden en c) frequentie volgens de dienstregeling. Laat zien waar metro\'s en trams snel of juist langzaam zijn, inclusief stilstandstijd per halte. Dikte van de lijn toont aantal voertuigen per uur. Bruikbaar voor alle OV-modaliteiten: metro, tram, bus, trein. De visualisatie combineert drie essentiële aspecten van OV-kwaliteit in één overzicht: de snelheid waarmee voertuigen tussen haltes rijden, de tijd die ze stilstaan bij haltes, en de frequentie van de dienstverlening. Deze methodologie maakt het mogelijk om knelpunten in het OV-netwerk te identificeren en de prestaties van verschillende lijnen te vergelijken. Voorbeeld toepassing voor Breda en omgeving met mogelijkheid om te kiezen voor verschillende tijdsperiodes (ochtendspits, zondagmiddag).',
    author: 'Roland Kager',
    date: '2024-08-17',
    category: 'transit',
    tags: ['GTFS', 'Kaarten', 'OV', 'Snelheid', 'Frequentie'],
    wordCount: 1040,
    imagesAvailable: 9,
    previewImage: '/assets/innoflashes/innoflash18-preview.webp',
    pdfPath: '/innoflashes/18/original.pdf',
    pdfSizeM: 1.04,
    relatedTools: ['ov-speed-frequency'],
    additionalImages: [
      '/innoflashes/18/speed-legend.png',
      '/innoflashes/18/breda-detail.png',
      '/innoflashes/18/breda-peak.webp',
    ],
    methodology: 'Combines GTFS schedule data with network geometry to calculate corridor speeds, dwell times, and service frequency. Line thickness represents vehicles per hour, color indicates speed categories.',
    realWorldExample: 'Available as Transit Speed Analysis tool on tools.studiobereikbaar.nl. Used for analyzing Amsterdam metro, tram, and bus networks.',
  },
  {
    id: '17',
    filename: '25-09-07_InnoFlash17_SB_ODIN_viewer_HANDLEIDING.md',
    title: 'ODiN Viewer - Handleiding',
    description: 'Handleiding voor de ODiN Viewer tool die OV-gebruik en reisgedrag inzichtelijk maakt. Bevat uitgebreide instructies voor het werken met ODiN-data in QlikView-omgeving.',
    fullDescription: 'De ODiN-viewer is een QlikView-tool die laagdrempelig toegang biedt tot het ODiN (Onderweg in Nederland) bestand voor gebruik in projecten. Het ODiN bestand bevat gegevens over OV-gebruik en reisgedrag van Nederlanders. De viewer maakt het mogelijk om deze complexe datasets eenvoudig te analyseren en te visualiseren zonder diepgaande technische kennis. De handleiding bevat uitgebreide instructies voor het werken met de viewer, inclusief het interpreteren van variabelen en het maken van analyses. De tool is beschikbaar op de HiDrive en vereist een QlikView-licentie of kan gebruikt worden op de QlikView-laptop op kantoor.',
    author: 'Roland Kager',
    date: '2024-09-07',
    category: 'tools',
    tags: ['Data', 'ODiN', 'OV', 'Viewer', 'QlikView'],
    wordCount: 4702,
    imagesAvailable: 3,
    previewImage: '/assets/innoflashes/innoflash17-preview.webp',
    pdfPath: '/innoflashes/17/original.pdf',
    pdfSizeM: 1.29,
    relatedTools: [],
  },
  {
    id: '16',
    filename: '25-02-12_InnovatieFlash16_Wijkvoorzieningen_Tekst.md',
    title: 'Wijkvoorzieningen: Nabijheidsindicator voor zes dagelijkse voorzieningen',
    description: 'Indicator voor wijkvoorzieningen op loopafstand met OpenStreetMap data. Meet aanwezigheid van zes voorzieningen binnen 1km en toont waar nieuwe woningen bouwen nabij bestaande voorzieningen optimaal is.',
    fullDescription: 'Een nieuwe indicator voor wijkvoorzieningen gebaseerd op OpenStreetMap data, ontwikkeld voor Dashboard Limburg en Brainportregio. De indicator meet de nabijheid van zes dagelijks gebruikte voorzieningen binnen 1km loopafstand: supermarkten, apotheken, kappers, scholen, bibliotheken en wijkcentra. Deze zes "indicator species" vertegenwoordigen het bredere scala aan wijkvoorzieningen. De indicator laat zien waar nieuwe woningen gebouwd kunnen worden nabij bestaande voorzieningen, waarbij a) bestaande voorzieningen beter renderen bij meer klanten, en b) minder nieuwe voorzieningen toegevoegd hoeven te worden. De methodologie werkt met gewogen scores per voorziening op basis van zeldzaamheid. Beschikbaar op zowel 500x500m als 100x100m grid-niveau.',
    author: 'Roland Kager',
    date: '2025-02-12',
    category: 'spatial',
    tags: ['Bereikbaarheid', 'Data', 'Kaarten', 'OpenStreetMap', 'QGIS', 'Voorzieningen'],
    wordCount: 1564,
    imagesAvailable: 11,
    previewImage: '/assets/innoflashes/innoflash16-preview.webp',
    pdfPath: '/innoflashes/16/original.pdf',
    pdfSizeM: 0.64,
    relatedTools: [],
  },
  {
    id: '15',
    filename: '25-01-30_InnovatieFlash15_Tekst.md',
    title: 'OV-index (Deel 1): Nieuwe indicator voor OV-bereikbaarheid lange afstand',
    description: 'Work-in-progress OV-indicator als bouwsteen voor SB-indicator vergelijkbaar met Nabijheidsindex. Meet bereikbare inwoners en banen binnen 60 min OV deur-tot-deur met focus op lange-afstand waar OV impact maakt.',
    fullDescription: 'Work-in-progress aan een nieuwe OV-index als bouwsteen voor een toekomstige SB-indicator vergelijkbaar met de Nabijheidsindex. De indicator meet het aantal bereikbare inwoners en banen binnen 60 minuten OV-reistijd deur-tot-deur, met focus op lange-afstandsverplaatsingen (minimaal 7.5km) waar OV de meeste impact maakt. Belangrijke kenmerken: a) maximaal 1 overstap, b) aandacht voor frequentie via gewogen reistijdreductie bij meerdere reisopties, c) flexibele integratie van fiets in voor- en natransport, d) verschillende instellingen mogelijk voor diverse toepassingen. De methodologie onderscheidt drie OV-niveaus: stadsmodel (tot 7.5km), regionaal model zoals Woerden (7.5-150km), en internationale verbindingen zoals Frankfurt (>150km). Toont verrassende resultaten voor Randstad versus Ruhrgebiet en Vlaamse Ruit door combinatie van frequentie en polycentrische structuur.',
    author: 'Roland Kager',
    date: '2025-01-30',
    category: 'transit',
    tags: ['Bereikbaarheid', 'Data', 'Kaarten', 'OV', 'Indicator', 'Deur-tot-deur'],
    wordCount: 3677,
    imagesAvailable: 3,
    previewImage: '/assets/innoflashes/innoflash15-preview.webp',
    pdfPath: '/innoflashes/15/original.pdf',
    pdfSizeM: 0.88,
    relatedTools: [],
    additionalImages: [
      '/innoflashes/15/ovindex-levels.webp',
      '/innoflashes/15/ovindex-scenarios.webp',
      '/innoflashes/15/nl-be-de-scores.webp',
    ],
  },
  {
    id: '14',
    filename: 'Tekst_InnovatieFlash_14_European_Grid_System.md',
    title: 'European Grid System: Variabele resolutie zonering op basis van bebouwing',
    description: 'Fundamentele innovatie aan het V500-systeem met zones van wisselende resolutie (100m tot 4km) op basis van bebouwingsdichtheid. Gebruikt OpenStreetMap rasterkaart voor heel Europa en maakt wijkniveau analyses efficiënt mogelijk.',
    fullDescription: 'Een fundamentele innovatie aan het V500-zoneringssysteem met zones van wisselende resolutie gebaseerd op bebouwingsdichtheid. Het European Grid System gebruikt een OpenStreetMap rasterkaart (10x10m pixels) van de Universiteit Heidelberg voor heel Europa. Vijf belangrijke innovaties: 1) gebruik van OSM-rasterkaart voor automatische zone-indeling, 2) algoritme voor gelaagd systeem (V100 tot V4km zones), 3) "adjusted centroid" als zwaartepunt ipv geometrisch middelpunt, 4) definitieve zoneringsgrootte in twee stappen via pixels én inwoners/banen, 5) overlappende zones van verschillende grootte mogelijk. Resultaat: ongeveer 155.000 zones voor Nederland (vergelijkbaar met V500) maar met fijnere resolutie in steden (100x100m) en grovere in landelijk gebied (tot 4x4km). Testomgeving Luxemburg toont robuustheid. Zorgt voor 1000x snellere berekeningen door slimme geometrie.',
    author: 'Roland Kager',
    date: '2025-01-01',
    category: 'spatial',
    tags: ['Data', 'Europa', 'Grid', 'Zonering', 'V500'],
    wordCount: 4203,
    imagesAvailable: 24,
    previewImage: '/assets/innoflashes/innoflash14-preview.webp',
    pdfPath: '/innoflashes/14/original.pdf',
    pdfSizeM: 3.55,
    relatedTools: [],
    additionalImages: [
      '/innoflashes/14/luxemburg-zones.png',
      '/innoflashes/14/eu-grid.png',
    ],
    methodology: 'Uses OSM raster data (10x10m pixels) to automatically split dense urban areas into 100m zones and aggregate sparse rural areas into 4km zones. "Adjusted centroids" ensure accurate representation of activity centers.',
    realWorldExample: 'Tested in Luxembourg environment. Enables cross-border European analysis, shifting from Netherlands-only to pan-European capabilities.',
  },
  {
    id: '12',
    filename: 'Innovatieflash 12_Tekst.md',
    title: 'Postcode-coördinaten: Verbeterde PC4-centroïdes en afstand tot stations',
    description: 'Handmatig verbeterde centroïdes voor alle 4000 PC4-zones op hoogste woningdichtheid of grootste bedrijventerrein. Inclusief algoritme voor "afstand tot station" met gewogen score bij meerdere nabije stations.',
    fullDescription: 'Handmatig verbeterde centroïdes voor alle 4000 PC4-zones in Nederland, geplaatst op hoogste woningdichtheid of grootste bedrijventerrein in plaats van geografisch middelpunt. Dit lost problemen op met PC4-gebieden die a) grote buitengebieden hebben met bewoning aan de rand, b) doughnut-vormen waar centroïde buiten gebied valt, of c) exclaves. Regels voor plaatsing: hoogste woningdichtheid, anders grootste bedrijventerrein, bij meerdere clusters het meest intensieve, leeg gebied in midden, nooit buiten shape of in water. Inclusief geavanceerd algoritme voor "afstand tot station" dat meerdere nabije stations weegt: bepaalt dichtstbijzijnde station, includeert "other" stations op basis van drempelwaarde voor extra vertrekkende treinen, definieert "referentie" en "karakteristiek" station met gewogen afstand en frequenties. Data beschikbaar voor 2006 en 2018.',
    author: 'Roland Kager',
    date: '2024-07-01',
    category: 'spatial',
    tags: ['Data', 'Postcode', 'PC4', 'Centroide', 'Stations'],
    wordCount: 4141,
    imagesAvailable: 5,
    previewImage: '/assets/innoflashes/innoflash12-preview.webp',
    pdfPath: '/innoflashes/12/original.pdf',
    pdfSizeM: 2.34,
    relatedTools: [],
    additionalImages: [
      '/innoflashes/12/pc4-centroids.webp',
      '/innoflashes/12/station-distance.webp',
      '/innoflashes/12/station-ranking.webp',
    ],
  },
  {
    id: '11',
    filename: '24-06-06_InnovatieFlash11_als_PDF.md',
    title: 'Nabijheidsdata 2004-2022-2040: Nieuwe clustering NL4000 en NL0500',
    description: 'Uitbreiding nabijheidsdata met toekomstscenario 2040 uit WLO-actualisatie. Introductie NL4000 (~4000 zones, PC4-equivalent) en NL0500 (~500 zones, gemeente-equivalent): cirkel-vormige clusters van gelijke grootte, onafhankelijk van gemeentelijke herindelingen.',
    fullDescription: 'Uitbreiding van nabijheidsdata met vijf belangrijke innovaties: a) toevoeging van prognosejaar 2040 in variant L (laag) en H (hoog) uit WLO-actualisatie 2023, b) nieuw dataformat met meerdere jaren in één file (2004, 2022, 2040L, 2040H) voor eenvoudige vergelijking, c) nieuwe V500-clustering systemen NL4000 (~4000 zones, PC4-equivalent) en NL0500 (~500 zones, gemeente-equivalent) met cirkel-vormige zones van gelijke grootte, d) kant-en-klare aggregatie naar PC4, gemeente en provincie, e) beschikbaar op HiDrive met shapefiles en stylefiles. NL4000 zones zijn gemiddeld 7km² (straal 1.5km), NL0500 zones 30km² (straal 3.1km). Voordelen: onafhankelijk van gemeentelijke herindelingen, geen grenzen dwars door V500 zones, coordinaat-ID bevat X-Y coördinaat, >99% van inwoners en banen gedekt. Gebruikbaar via koppeltabel of als shapefile met POINT/POLYGON.',
    author: 'Roland Kager',
    date: '2024-06-06',
    category: 'spatial',
    tags: ['Data', 'Nabijheidsindex', 'V500', 'Zonering', 'WLO', 'Toekomstscenario'],
    wordCount: 2410,
    imagesAvailable: 10,
    previewImage: '/assets/innoflashes/innoflash11-preview.webp',
    pdfPath: '/innoflashes/11/original.pdf',
    pdfSizeM: 0.96,
    relatedTools: [],
    additionalImages: [
      '/innoflashes/11/banen-2040-vs-2022.webp',
      '/innoflashes/11/nabijheid-2040-vs-2004.webp',
      '/innoflashes/11/inwoners-2040.webp',
    ],
    methodology: 'Integrates PBL 2040 WLO scenarios with historical CBS data (2004-2022). Creates circular V500 clusters of equal size, independent of municipal boundaries. Data format supports multi-year comparison in single file.',
    realWorldExample: 'Used in Stedendriehoek urbanization strategy (2025) with 49,836 calculation zones across 8 municipalities. Enables scenario comparison for 10 urbanization variants using WLO projections.',
  },
  {
    id: '10',
    filename: '_InnovatieFlash10_tekstje.txt',
    title: 'Nabijheidsindex 2004-2022: Visualisaties steden en regio\'s',
    description: 'Kant-en-klaar kaartjesset met nabijheidsklasse visualisaties voor 15 steden in identieke schaal van 30x30km voor jaren 2004, 2010, 2016 en 2022. Toont geleidelijke transitie per stad en regionale ontwikkelingen in Dashboard Verstedelijking-classificatie.',
    fullDescription: 'Een complete set kant-en-klare kaartvisualisaties van de nabijheidsklasse ontwikkeling voor 15 Nederlandse steden en hun regio\'s. Alle kaarten zijn gemaakt in identieke schaal van 30x30km voor de jaren 2004, 2010, 2016 en 2022, wat directe vergelijking mogelijk maakt. De kaarten tonen de geleidelijke transitie van verstedelijking per stad volgens de Dashboard Verstedelijking-classificatie (hoogstedelijk, stedelijk, semi-stedelijk, centrum-dorps, agrarisch-dorps, ruraal). Door de consistente schaal en tijdreeks wordt de regionale ontwikkeling zichtbaar: uitbreiding van stedelijk gebied, verdichting in bestaand bebouwd gebied, en verschuivingen tussen nabijheidsklassen. Totaal 114 afbeeldingen beschikbaar (15 steden × 4 jaren + regionale overzichten). Geschikt voor presentaties, rapporten en vergelijkende analyses van verstedelijkingstrends.',
    author: 'Roland Kager',
    date: '2023-02-01',
    category: 'spatial',
    tags: ['Nabijheidsindex', 'Kaarten', 'Verstedelijking', 'V500', 'Timeline'],
    wordCount: 400,
    imagesAvailable: 114,
    previewImage: '/assets/innoflashes/innoflash10-preview.webp',
    pdfPath: '/innoflashes/10/original.pdf',
    pdfSizeM: 0.49,
    relatedTools: [],
    additionalImages: [
      '/innoflashes/10/amsterdam-2004.webp',
      '/innoflashes/10/amsterdam-2022.webp',
      '/innoflashes/10/zwolle-2004.webp',
    ],
  },
  {
    id: '09',
    filename: 'InnovatieFlash09_Verhuisdata.md',
    title: 'Verhuisdata 2004-2022: Relocatie-patronen tussen gemeenten en COROP-zones',
    description: 'Opgeschoonde CBS-verhuisdata op SB-Database-Bereikbaar met aantal verhuizingen per jaar tussen alle gemeenten en COROP-zones. Inclusief "factor" die toont waar verhuizingen meer of minder zijn dan verwacht op basis van afstand en inwoners.',
    fullDescription: 'Opgeschoonde en verrijkte CBS-verhuisdata voor 2004-2022 op de SB-Database-Bereikbaar. Bevat aantal verhuizingen per jaar tussen alle combinaties van gemeenten en COROP-zones (3.88 miljoen records). Belangrijkste datavelden: aantal verhuizingen, verhuisratio per miljoen inwoners, verwachte verhuisratio op basis van afstand, en cruciale "factor" die aangeeft waar verhuizingen significant meer of minder zijn dan verwacht. Drie aggregatieniveaus: gemeente-gemeente, gemeente-COROP, COROP-NL (3×3=9 analyses mogelijk). Inclusief hulpbestanden met geo-shapes en statistieken per gemeente/COROP per jaar. Data gekoppeld aan nabijheidsindex, aantal inwoners, banen, WOZ-waarde, leeftijdsverdeling. Visualisaties tonen hemelsbreede lijnen van herkomst naar bestemming. Geschikt voor analyses van verhuisbewegingen, regionale aantrekkingskracht, en ruimtelijke ontwikkelingen. Gebruik Query Builder voor filtering op jaar, afstand, aantal verhuizingen.',
    author: 'Roland Kager',
    date: '2023-10-01',
    category: 'data',
    tags: ['CBS', 'Data', 'Verhuizingen', 'DatabaseBereikbaar', 'Gemeenten', 'COROP'],
    wordCount: 2572,
    imagesAvailable: 2,
    previewImage: '/assets/innoflashes/innoflash09-preview.webp',
    pdfPath: '/innoflashes/09/original.pdf',
    pdfSizeM: 0.31,
    additionalImages: [
      '/innoflashes/09/migration-dashboard.webp',
      '/innoflashes/09/migration-flows.webp',
      '/innoflashes/09/corop-factors.webp',
    ],
    relatedTools: [],
  },
  {
    id: '08',
    filename: 'InnovatieFlash08_Illustraties.md',
    title: 'Achtergrond Shapes + OSM: Referentie voor ondergrond-lagen',
    description: 'Referentielijst van beschikbare achtergrond-lagen voor QGIS-visualisaties: water, woningen, voorzieningen, industrie en grenzen. Eenvoudige notatie met schema-namen en layer-codes voor consistente kaartopmaak.',
    fullDescription: 'Twee sets basis-ondergrondlagen voor QGIS-visualisaties nu beschikbaar op DatabaseBereikbaar: a) Basis-achtergrondlagen (water, woningen, voorzieningen, industrie, grenzen) en b) OSM-tabellen voor loop-, fiets- en autoroutes. Achtergrondlagen: PBL_2013_Hoofdwater (rivieren, meren), BBG_2017_Woning (bebouwing, winkels), BBG_2017_Industrie (bedrijventerreinen), BBG_2017_Totaal (alle grondgebruik met 20+ codes). OSM-data: gesplitst in NODES en WAYS, per vervoerwijze (WALK, BIKE, EBIKE, CAR), unimodaal of totaal, 9 overlappende deelgebieden tussen Middelburg-Lelystad-Eindhoven. Data uit februari 2023. Zeer snel te berekenen (10 seconden voor Luxemburg) door gelaagde vierkantzone-structuur. Elke shapefile bevat datavelden voor snelheid, reistijd, connectoren. Bruikbaar voor visualisaties, routeberekeningen, en multimodale analyses.',
    author: 'Roland Kager',
    date: '2023-07-19',
    category: 'tools',
    tags: ['QGIS', 'Visualisatie', 'OpenStreetMap', 'Achtergrond', 'Kaarten'],
    wordCount: 60,
    imagesAvailable: 2,
    previewImage: '/assets/innoflashes/innoflash08-preview.webp',
    pdfPath: '/innoflashes/08/original.pdf',
    pdfSizeM: 1.03,
    relatedTools: [],
    additionalImages: [
      '/innoflashes/08/reference-layers.webp',
      '/innoflashes/08/osm-routes.webp',
      '/innoflashes/08/background-catalog.webp',
    ],
  },
  {
    id: '07',
    filename: '_InnovatieFlash07_Mail.md',
    title: 'Shapefiles BusTramMetro uit GTFS - nu op DatabaseBereikbaar',
    description: 'GTFS-data omgezet naar shapefiles van werkelijke haltes en OV-lijnen voor visualisatie, beschikbaar op DatabaseBereikbaar. Onderscheidt frequente buslijnen, plusbussen, uurlijnen, buurtbussen, tram en veer met metadata per lijn.',
    fullDescription: 'GTFS-data omgezet naar gedetailleerde shapefiles van werkelijke haltes en OV-lijnen voor visualisatie, beschikbaar op DatabaseBereikbaar in schema 06b_Netwerk_GTFS. Twee geo-lagen: a) Geoptimaliseerde GTFS-Haltes (13.500 ipv 60.000, max 1 per V500-zone) met modaliteiten, OV-scores, bedieningsklassen, en Trip/Route-IDs, b) GTFS-Links tussen haltes met vervoerwijze, frequentie, afstand, snelheid en Trip-IDs. Onderscheidt tram, veer, frequente bus (2x/uur), plusbus (3x/uur + 32.5km/u), uurlijn, en buurtbus. Dikte lijn toont frequentie, kleur modaliteit. Beschikbaar voor drie dienstregelingen: ochtendspits (do_07_08), avond (do_20_21), zondag (zo_09_10). Historische dienstregelingen mogelijk vanaf maart 2014. 25 kant-en-klare visualisaties voor vijf regio\'s × vijf tijdsperiodes op Sharepoint. Volledig aanpasbaar in QGIS met eigen filters, kleuren en symbologie.',
    author: 'Roland Kager',
    date: '2023-07-01',
    category: 'transit',
    tags: ['Bereikbaarheid', 'Data', 'GTFS', 'Kaarten', 'OV', 'DatabaseBereikbaar'],
    wordCount: 2030,
    imagesAvailable: 25,
    previewImage: '/assets/innoflashes/innoflash07-preview.webp',
    relatedTools: ['ov-speed-frequency', 'ov-passage-frequency'],
    additionalImages: [
      '/innoflashes/07/randstad-morning.webp',
      '/innoflashes/07/randstad-evening.webp',
      '/innoflashes/07/lelylijn-ochtend.webp',
    ],
    pdfPath: '/innoflashes/07/original.pdf',
    pdfSizeM: 1.97,
  },
  {
    id: '06',
    filename: 'InnoFlash06_CBS_WijkGem_data.md',
    title: 'CBS Wijk- en buurtdata: Visualisaties demografische ontwikkelingen',
    description: 'Kaartenserie met CBS wijk- en buurtgegevens voor jaren 2004, 2013 en 2022. Toont inwoners per hectare, vergrijzing, huishoudgrootte en WOZ-waarde voor analyse van demografische en sociaal-economische ontwikkelingen.',
    fullDescription: 'Complete kaartenserie met CBS wijk- en buurtgegevens voor de jaren 2004, 2013 en 2022, visualiseert demografische en sociaal-economische ontwikkelingen op wijkniveau. De serie bevat 26 afbeeldingen met thema\'s: inwoners per hectare (woningdichtheid), vergrijzing (percentage 65+), gemiddelde huishoudgrootte, en WOZ-waarde woningen. Deze visualisaties maken het mogelijk om ruimtelijke patronen en tijdreeksen te analyseren: waar verdichting plaatsvindt, welke wijken vergrijzen, waar huishoudensverdunning optreedt, en hoe vastgoedwaarden zich ontwikkelen. De CBS wijk- en buurtindeling biedt fijnmaziger detail dan gemeenteniveau en is geschikt voor lokale analyses. Data beschikbaar voor heel Nederland, bruikbaar voor beleidsanalyses, woningmarktonderzoek, en demografische studies. Kaarten tonen duidelijk de verschillen tussen stedelijk en landelijk gebied, en tussen groei- enkrimpgebieden.',
    author: 'Roland Kager',
    date: '2023-03-01',
    category: 'data',
    tags: ['CBS', 'Data', 'Demografie', 'Kaarten', 'WOZ', 'Vergrijzing'],
    wordCount: 300,
    imagesAvailable: 26,
    previewImage: '/assets/innoflashes/innoflash06-preview.webp',
    pdfPath: '/innoflashes/06/original.pdf',
    pdfSizeM: 1.12,
    relatedTools: [],
  },
  {
    id: '05',
    filename: 'InnoFlash05_Loopstromen.md',
    title: 'Loopstromen: Voetgangersroutes en loopbaarheid',
    description: 'Visualisatie van looproutes en voetgangerstromen in stedelijk gebied. Toont loopbaarheid en belangrijkste loopverbindingen voor analyse van voetgangersaccessibiliteit en stedelijk ontwerp.',
    fullDescription: 'Analyse en visualisatie van looproutes en voetgangerstromen in stedelijk gebied op basis van OpenStreetMap-data. De methodologie identificeert belangrijke loopverbindingen en berekent loopbaarheid van gebieden rekening houdend met de kwaliteit van voetpaden, trottoirs, en voetgangersvoorzieningen. Toont welke routes voetgangers gebruiken om van A naar B te komen, waar loopstromen samenkomen, en welke gebieden goed of slecht toegankelijk zijn voor voetgangers. Relevant voor stedelijk ontwerp, inrichting openbare ruimte, verkeersveiligheid, en toegankelijkheidsanalyses. De visualisaties ondersteunen beslissingen over voetgangersvoorzieningen, locatiekeuze voorzieningen op loopafstand, en identificatie van barrières in het voetgangersnetwerk. Bruikbaar in combinatie met andere modaliteiten voor multimodale bereikbaarheidsanalyses.',
    author: 'Roland Kager',
    date: '2023-02-20',
    category: 'spatial',
    tags: ['Loopbaarheid', 'Voetgangers', 'Kaarten', 'Accessibility', 'Stedelijk'],
    wordCount: 250,
    imagesAvailable: 4,
    previewImage: '/assets/innoflashes/innoflash05-preview.webp',
    relatedTools: [],
    additionalImages: [
      '/innoflashes/05/tilburg-zoom-basis.webp',
      '/innoflashes/05/tilburg-zoom-in.webp',
      '/innoflashes/05/tilburg-zoom-out.webp',
    ],
    pdfPath: '/innoflashes/05/original.pdf',
    pdfSizeM: 0.11,
  },
  {
    id: '04',
    filename: 'InnovatieFlash04_OvScore.md',
    title: 'OV-Score: Bedieningskwaliteit en OV-Bereikbaarheid (2014-2023)',
    description: 'Kaartjes en data over bedieningskwaliteit OV per locatie. Toont aantal bereikbare banen binnen 2 uur met OV inclusief penalties voor overstappen, en meet verbetering van 2014 tot 2023 per postcode-4 gebied.',
    fullDescription: 'Innovatieve OV-Score die de bedieningskwaliteit van het OV meet als aantal bereikbare banen (FTE) binnen gewogen reistijd van 90 minuten met het OV. Definitie: banen tot 60 min tellen volledig mee, tussen 60-120 min lineair afnemend van 1.0 naar 0.0. Drie cruciale penalties: a) 3 minuten per overstap, b) 10 minuten per belbus, c) frequentie-penalty van 1/3 van maximale wachttijd boven 6 minuten "metrofrequentie". Bijvoorbeeld: uurdienst krijgt 24 min penalty, kwartierdienst 3 min. Data beschikbaar voor vier tijdsperiodes (ochtendspits, avond, zaterdag, zondag) en twee jaren (2014, 2023). Kaarten en QlikView-dashboard tonen OV-kwaliteit per PC4-gebied voor alle provincies en gemeenten. Resultaten: landelijk +11% verbetering 2014-2023, gemeente Utrecht +57%, vooral door PHS-IC, R-net, en nieuwe stations (Holendrecht, Science Park, Poort). OV-lijnenkaarten aangepast aan historische netwerken.',
    author: 'Roland Kager',
    date: '2023-02-16',
    category: 'transit',
    tags: ['Bereikbaarheid', 'Data', 'Kaarten', 'OV', 'QGIS', 'Score', 'Timeline'],
    wordCount: 3463,
    imagesAvailable: 18,
    previewImage: '/assets/innoflashes/innoflash04-preview.webp',
    pdfPath: '/innoflashes/04/original.pdf',
    pdfSizeM: 0.98,
    relatedTools: [],
    additionalImages: [
      '/innoflashes/04/nl-2023-ochtend.webp',
      '/innoflashes/04/nl-2014-ochtend.webp',
      '/innoflashes/04/nl-2023-45min.webp',
    ],
  },
  {
    id: '03',
    filename: '23-02-06_InnovatieFlash3_PDF_van_de_mail.md',
    title: 'BAG-data aggregatie naar 500x500m niveau',
    description: 'BAG-data geaggregeerd van 7GB gebouwniveau naar V500-zones met dominante bouwperiode, bebouwingspercentage en aantal hectare niet-woonfuncties. Superactueel met maandelijkse updates en beschikbaar als shapefile.',
    fullDescription: 'BAG-data (Basisregistratie Adressen en Gebouwen) geaggregeerd van 7GB gebouwniveau naar handelbaarV500-zones (500x500m). Het BAG-bestand is superactueel met maandelijkse updates (deze versie: 1 januari 2023) en bevat per gebouw: bouwjaar, gebruiksfunctie, aantal units, ground space, floor space. Zeven visualisaties: 1) Karakteristiek bouwperiode (12 categorieën, dominante periode als >40%), 2) Karakteristieke oudste bouwperiode (oudere jaren zwaarder gewogen), 3) Karakteristieke nieuwste bouwperiode (nieuwe jaren zwaarder gewogen), 4) Bebouwingspercentage (ground space / celoppervlak), 5) Aantal hectare niet-woonfuncties, 6) Aantal hectare kantoor+industrie, 7) Aantal bouwlagen (floor space / ground space). Aggregatie maakt vergelijking "grote lijn" mogelijk en koppeling met andere datasets. Shapefile 65MB (ipv 7GB). Inclusief inwoners, woningen, banen uit CBS voor cross-validatie.',
    author: 'Roland Kager',
    date: '2023-02-06',
    category: 'data',
    tags: ['BAG', 'Bouwjaar', 'Data', 'Gebouwen', 'V500'],
    wordCount: 1856,
    imagesAvailable: 22,
    previewImage: '/assets/innoflashes/innoflash03-preview.webp',
    pdfPath: '/innoflashes/03/original.pdf',
    pdfSizeM: 1.73,
    relatedTools: [],
  },
  {
    id: '02',
    filename: '23-01-30_Mail_InnovatieFlash01-OpenOVdata.md',
    title: 'OV-visualisaties en OV-reistijdisochronen',
    description: 'Doorbraak in werken met OV-data: visualiseren van dienstregelingen uit GTFS en isochronen voor snelste reistijden vanaf startlocatie. Werkt met historische dienstregelingen vanaf 2014 en alle tijdsperioden.',
    fullDescription: 'Doorbraak in werken met OV-data mogelijk gemaakt door V500-Clusters algoritme uit InnovatieFlash 1. Drie producten: 1) Visualisatie van OV-dienstregelingen - twee geo-lagen: geoptimaliseerde GTFS-haltes (13.500 ipv 60.000) met OV-scores en bedieningsklassen, en GTFS-Links met modaliteit, frequentie, snelheid per corridor. Kleur toont modaliteit, dikte toont frequentie. 2a) GeoTabel GTFS-Conns met complete OV-verbindingen (max 3 overstappen) tussen V500-Clusters, toont snelste reistijden per starthalte met gekleurde stippen (groen <60min, oranje 60-90min, rood 90-120min, paars 120-150min). 2b) GeoTabel GTFS-Conns-Aggr met aggregatie naar aantal verbindingen, gemiddelde reistijd, en frequentie-penalty (1/3 van langste wachttijd -6min). Werkt met historische dienstregelingen vanaf maart 2014, alle tijdsperioden, en kan lijnen handmatig toevoegen/verwijderen voor effectstudies (Lelylijn, Hubs). Voorbeelden: Middelburg, Utrecht, Stadskanaal.',
    author: 'Roland Kager',
    date: '2023-01-30',
    category: 'transit',
    tags: ['Bereikbaarheid', 'Data', 'OV', 'GTFS', 'Isochronen', 'Visualisatie'],
    wordCount: 3136,
    imagesAvailable: 18,
    previewImage: '/assets/innoflashes/innoflash02-preview.webp',
    pdfPath: '/innoflashes/02/original.pdf',
    pdfSizeM: 0.40,
    relatedTools: ['multimodal-catchment'],
    additionalImages: [
      '/innoflashes/02/utrecht-isochrones.webp',
      '/innoflashes/02/rotterdam-network.webp',
      '/innoflashes/02/stadskanaal-wait.webp',
    ],
  },
  {
    id: '01',
    filename: '_InnoFlash01_V500_ClusterZones.md',
    title: 'V500 Clustering-algoritme: Van 145k zones naar 3500 ronde clusters',
    description: 'Clustering-algoritme dat 145.000 V500-zones slim clustert tot 3.500 grotere zones vergelijkbaar met PC4. Kenmerken zijn ronde vormen, betekenisvolle centra en flexibel criterium voor analyses waar V500 te fijnmazig is.',
    fullDescription: 'Innovatief clustering-algoritme dat 145.000 V500-zones (500x500m) slim clustert tot ongeveer 3.500 grotere zones vergelijkbaar met PC4-niveau. Ontwikkeld om OV-algoritmes te versnellen en analyses mogelijk te maken waar V500 te fijnmazig is. Wensenlijstje: a) ongeveer 4000 zones (PC4-equivalent) met 30-60 V500-zones per cluster, b) ronde vormen (minimale afstanden centroïde-randen), c) zwaarder wegen van bevolkte gebieden, d) >99% inwoners en banen gedekt, e) aaneengesloten clusters zonder losse zones, f) betekenisvol centrum (bestaand cluster inwoners/banen, niet toevallig middelpunt), g) flexibel criterium (inwoners+banen, maar ook woningen, m² groen, OV-haltes mogelijk). Algoritme stabiel, resulteert in 5 minuten voor heel Nederland. Database-tabel en shapefile beschikbaar. Essentieel voor snellere berekeningen: 145k × 145k = 24 miljard combinaties wordt 3.5k × 3.5k = 12 miljoen. Fundamentele bouwsteen voor alle latere OV-algoritmes.',
    author: 'Roland Kager',
    date: '2022-12-20',
    category: 'spatial',
    tags: ['Bereikbaarheid', 'Data', 'Zonering', 'V500', 'Clustering'],
    wordCount: 1140,
    imagesAvailable: 1,
    previewImage: '/assets/innoflashes/innoflash01-preview.webp',
    pdfPath: '/innoflashes/01/original.pdf',
    pdfSizeM: 0.88,
    additionalImages: [
      '/innoflashes/01/cluster-overview.webp',
      '/innoflashes/01/cluster-criteria.webp',
      '/innoflashes/01/cluster-output.webp',
    ],
    relatedTools: [],
  },
];

// Category display names and colors
export const categoryConfig = {
  transit: {
    label: 'Transit Analysis',
    color: '#068AA1',
    bgColor: '#E6F4F7',
  },
  spatial: {
    label: 'Spatial Analysis',
    color: '#A5CE39',
    bgColor: '#F7FAE9',
  },
  data: {
    label: 'Data Processing',
    color: '#E62E73',
    bgColor: '#FFE5EF',
  },
  tools: {
    label: 'Tools & Viewers',
    color: '#F5A623',
    bgColor: '#FFF4E6',
  },
  general: {
    label: 'General',
    color: '#7C8081',
    bgColor: '#F3F4F6',
  },
};

// Get InnoFlashes by category
export function getInnoFlashesByCategory(category: string): InnoFlash[] {
  return innoflashes.filter((flash) => flash.category === category);
}

// Get InnoFlashes by tag
export function getInnoFlashesByTag(tag: string): InnoFlash[] {
  return innoflashes.filter((flash) => flash.tags.includes(tag));
}

// Get InnoFlashes related to a data team service
export function getRelatedInnoFlashes(serviceId: string): InnoFlash[] {
  return innoflashes.filter(
    (flash) => flash.relatedTools && flash.relatedTools.includes(serviceId)
  );
}

// Get all unique tags
export function getAllTags(): string[] {
  const tags = new Set<string>();
  innoflashes.forEach((flash) => {
    flash.tags.forEach((tag) => tags.add(tag));
  });
  return Array.from(tags).sort();
}
